import { useMemo } from 'react';
import { Resource as ResourceFromResources } from '../../../hooks/api/useResources';
import { Resource as ResourceFromExperiences } from '../../../hooks/api/useExperiences';
import { ProviderResource } from '../../../hooks/utils/useProviderResources';
import { ProviderName } from '../../../hooks/api/useExternalProviderPlans';
import { useProviderPlan } from '../../../hooks/api/useExternalProviderPlans';
import { useProviderDetails } from './useProviderDetails';
import { getConfiguredContentHtml } from '../../../hooks/utils/providerPlanDetails';

// Union type that accepts either Resource type, with optional properties for compatibility
export type Resource = (ResourceFromResources | ResourceFromExperiences) & {
  tag_group_names?: string[];
  rating?: number;
};

export interface UseResourceModalDataProps {
  resource?: Resource;
  providerResource?: ProviderResource;
  providerName?: ProviderName;
}

export interface UseResourceModalDataReturn {
  resource: Resource | undefined;
  isProviderResource: boolean;
  providerType: string | undefined;
  finalProviderName: ProviderName | undefined;
  contentHtml: string | undefined;
  partnerResourceConfig: ReturnType<typeof useProviderDetails>['partnerResourceConfig'];
  ssoLinkLoading: boolean;
  ssoError: boolean;
  url: string | undefined;
  finalDetectedProviderName: ProviderName | undefined;
  urlError: boolean;
  errorMessage: string | undefined;
  displayPhoneNumber: boolean;
  isGlobal: boolean;
}

export const useResourceModalData = ({
  resource: resourceProp,
  providerResource,
  providerName,
}: UseResourceModalDataProps): UseResourceModalDataReturn => {
  const { providerPlan, providerName: planProviderName } = useProviderPlan();

  const isProviderResource = !!providerResource;
  const providerType = providerResource?.providerType;
  const finalProviderName = providerName || planProviderName;

  const {
    providerName: detectedProviderName,
    ssoLinkLoading,
    ssoError,
    url,
    partnerResourceConfig,
  } = useProviderDetails(providerType);

  const contentHtml = useMemo(() => {
    if (!isProviderResource || !providerType) {
      return undefined;
    }
    return getConfiguredContentHtml(providerPlan, providerType);
  }, [isProviderResource, providerPlan, providerType]);

  const resource = useMemo(() => {
    if (providerResource) {
      return {
        id: 0,
        name: providerResource.name,
        description: providerResource.description,
        long_description: providerResource.description,
        image_url: providerResource.image_url,
        url: providerResource.url,
        url_description: providerResource.url_description,
        phone_number: undefined,
        tag_group_names: providerResource.tag_names,
        tag_names: providerResource.tag_names,
        client_id: null,
        rating: undefined,
        challenge_areas: [],
        subject_areas: [],
        indexed_at: null,
        meta_keywords: '',
        notes: '',
        sort_weight: 0,
        status: 'live' as const,
        image_file_path: null,
        wide_image_file_path: null,
        wide_image_url: null,
        text_number: null,
        is_pre_crisis: false,
      } as unknown as Resource;
    }
    return resourceProp;
  }, [providerResource, resourceProp]);

  const isGlobal = !resource?.client_id;
  const finalDetectedProviderName = providerName || (isProviderResource ? detectedProviderName : undefined);
  const urlError = isProviderResource && (ssoError || !url);
  const urlParams = new URLSearchParams(url?.split('?')[1]);
  const errorMessage = urlParams.get('error_message') ? decodeURIComponent(urlParams.get('error_message')!) : undefined;
  const displayPhoneNumber = !isProviderResource;

  return {
    resource,
    isProviderResource,
    providerType,
    finalProviderName,
    contentHtml,
    partnerResourceConfig,
    ssoLinkLoading: Boolean(ssoLinkLoading),
    ssoError: Boolean(ssoError),
    url,
    finalDetectedProviderName,
    urlError: Boolean(urlError),
    errorMessage,
    displayPhoneNumber: Boolean(displayPhoneNumber),
    isGlobal: Boolean(isGlobal),
  };
};
